/**
 * `org` procedure group. Org == tenant; there is no second concept.
 *
 * `organizations` / `members` are better-auth owned; `org_settings` is our sidecar.
 */

import { z } from 'zod';
import {
  isoDateTimeSchema,
  jsonObjectSchema,
  nonBlankString,
  timezoneSchema,
  uuidSchema,
} from './common.ts';
import { orgRoleSchema, recordingConsentModeSchema } from './enums.ts';

/**
 * `[a-z0-9]` with internal hyphens; the tenant's URL segment. Applied to *input* only
 * — responses echo whatever is already stored, so a legacy slug can never make a
 * perfectly good org fail response validation.
 */
export const orgSlugSchema = z
  .string()
  .min(2)
  .max(63)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'expected a lowercase slug');

export const organizationSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  slug: z.string(),
  logo: z.url().nullable(),
  metadata: jsonObjectSchema.nullable(),
  createdAt: isoDateTimeSchema,
});

/** A row of `org.list`: the org plus the caller's standing in it. */
export const orgMembershipSchema = organizationSchema.extend({
  role: orgRoleSchema,
  memberCount: z.int().min(0),
  isActive: z.boolean(),
  joinedAt: isoDateTimeSchema,
});

export const orgSettingsSchema = z.object({
  orgId: uuidSchema,
  timezone: timezoneSchema,
  enrichmentMonthlyCreditCap: z.int().min(0),
  enrichmentAutoOnCreate: z.boolean(),
  /**
   * Whether vendor-enriched fields may cross a share grant. Legal default is OFF;
   * see the enrichment audit — payloads are licensed per tenant.
   */
  allowEnrichedFieldSharing: z.boolean(),
  llmMonthlyTokenCapTop: z.int().min(0),
  recordingDefaultConsentMode: recordingConsentModeSchema,
  /**
   * The org's assertion that its meetings fall under ALL-PARTY consent.
   *
   * Jurisdiction is underivable from a calendar event — attendee domains say
   * nothing about where anyone sits, and one timezone spans states with opposite
   * rules — so `disclose_two_party_only` needs this input to be decidable at
   * all. Defaults TRUE: a workspace that never touches it discloses on every
   * recording, which is the direction that is merely annoying when wrong.
   */
  recordingAllPartyConsentAssumed: z.boolean(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

// --- inputs ------------------------------------------------------------------

export const orgListInputSchema = z.object({
  includeInactive: z.boolean().default(false),
});

export const orgGetInputSchema = z.object({
  orgId: uuidSchema,
});

export const orgCreateInputSchema = z.object({
  name: nonBlankString(200, 'name'),
  /** Omitted means "derive from name"; collisions are resolved server-side. */
  slug: orgSlugSchema.optional(),
  timezone: timezoneSchema.optional(),
});

export const orgSettingsGetInputSchema = z.object({
  orgId: uuidSchema,
});

/**
 * PATCH semantics: absent keys are untouched. No "at least one key" refinement — a
 * no-op patch is harmless, and a top-level refinement would break oRPC's path-param
 * extraction for `/orgs/{orgId}/settings`.
 */
export const orgSettingsUpdateInputSchema = z.object({
  orgId: uuidSchema,
  timezone: timezoneSchema.optional(),
  enrichmentMonthlyCreditCap: z.int().min(0).max(1_000_000).optional(),
  enrichmentAutoOnCreate: z.boolean().optional(),
  allowEnrichedFieldSharing: z.boolean().optional(),
  llmMonthlyTokenCapTop: z.int().min(0).optional(),
  recordingDefaultConsentMode: recordingConsentModeSchema.optional(),
  recordingAllPartyConsentAssumed: z.boolean().optional(),
});

export const orgSwitchActiveInputSchema = z.object({
  orgId: uuidSchema,
});

export const orgSwitchActiveOutputSchema = z.object({
  orgId: uuidSchema,
  role: orgRoleSchema,
  switchedAt: isoDateTimeSchema,
});

export const orgListOutputSchema = z.object({
  items: z.array(orgMembershipSchema),
  activeOrgId: uuidSchema.nullable(),
});

// --- inferred types ----------------------------------------------------------

export type Organization = z.output<typeof organizationSchema>;
export type OrgMembership = z.output<typeof orgMembershipSchema>;
export type OrgSettings = z.output<typeof orgSettingsSchema>;
export type OrgCreateInput = z.input<typeof orgCreateInputSchema>;
export type OrgSettingsUpdateInput = z.input<typeof orgSettingsUpdateInputSchema>;
